<template>
  <div>
    <div class="d-flex flex-row align-center mb-6">
      <p class="text-2xl mb-0">Consolidation financière</p>
      <rmr-icon-button
        :icon="icons.mdiHelpCircleOutline"
        tooltip="Comprendre les chiffres de ce rapport"
        class="ml-2"
        @click="showHelp = true"
      />
    </div>

    <consolidation-help-modal v-model="showHelp" />

    <v-form>
      <v-card class="mb-6" v-show="isVisibleStep(0)">
        <v-card-title>Initialisation</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-container class="text-center">
                <v-select
                  outlined
                  dense
                  v-model="initMode"
                  :items="initModes"
                  :menu-props="{ maxHeight: '400', zIndex: 13 }"
                  label="Configuration"
                  item-text="name"
                  item-value="id"
                  @keyup="checkNext('step-0-0')"
                  :disabled="isSubStepDisabled(0, 0)"
                >
                  <!-- Template to render selected data -->
                  <template slot="selection" slot-scope="data">
                    <v-chip
                      :inputValue="data.selected"
                      close
                      class="chip--select-multi"
                      @input="remove(data.item)"
                    >
                      {{ data.item.name }}
                    </v-chip>
                  </template>
                  <!-- Template to render data / optgroup when the select is expanded -->
                  <template slot="item" slot-scope="data">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{
                          typeof data.item === "object"
                            ? data.item.name /* Normal item */
                            : data.item /* Divider and header */
                        }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-container>
            </v-col>

            <v-col
              cols="12"
              v-if="showJsonConfigProcess"
              v-show="isVisibleSubStep(0, 1)"
            >
              <v-container class="text-center">
                <v-textarea
                  outlined
                  v-model="config_json"
                  label="Config JSON"
                  @keyup="checkNext('step-0-1')"
                  :disabled="isSubStepDisabled(0, 1)"
                ></v-textarea>
              </v-container>
            </v-col>
          </v-row>

          <prev-next
            v-if="steps.current.step === 0"
            :canPrev="canPrev"
            :canNext="canNext"
            :canReset="canReset"
            @prevStep="prevStep"
            @nextStep="nextStep"
            @resetStep="resetStep"
          />
        </v-card-text>
      </v-card>

      <v-card class="mb-6" v-show="isVisibleStep(1)">
        <v-card-title>Configuration</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.export_file_name"
                  dense
                  label="Nom du fichier qui sera téléchargé"
                  @keyup="checkNext('step-1-0')"
                  :disabled="isSubStepDisabled(1, 0)"
                ></v-text-field>
              </v-container>
            </v-col>
            <v-col cols="12" v-show="isVisibleSubStep(1, 1)">
              <v-container class="text-center">
                <date-filter
                  :initialValue="config.monthDate"
                  format="month"
                  :isRange="false"
                  @valueChanged="
                    (val) => {
                      updateMonthDate(val);
                      checkNext('step-1-1');
                    }
                  "
                  :disabled="isSubStepDisabled(1, 1)"
                  periodType="past-only"
                />
              </v-container>
            </v-col>
            <v-col cols="12" v-show="isVisibleSubStep(1, 2)">
              <v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.currency_conversion_rate"
                  type="number"
                  dense
                  label="Taux de conversion USD => Euro"
                  @keyup="checkNext('step-1-2')"
                  :disabled="isSubStepDisabled(1, 2)"
                ></v-text-field>
              </v-container>
            </v-col>
          </v-row>

          <prev-next
            v-if="steps.current.step === 1"
            :canPrev="canPrev"
            :canNext="canNext"
            :canReset="canReset"
            @prevStep="prevStep"
            @nextStep="nextStep"
            @resetStep="resetStep"
          />
        </v-card-text>
      </v-card>

      <v-card class="mb-6" v-show="isVisibleStep(2)">
        <v-card-title>Données des plateformes</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.platformData.url"
                  dense
                  label="URL (google spreadsheet)"
                  @keyup="checkNext('step-2-0')"
                  :disabled="isSubStepDisabled(2, 0)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(2, 0) && !isSubStepDisabled(2, 0)"
              >
                Ce fichier doit être un fichier Excel.<br />
                Il doit contenir la répartition par plateforme sur 3 colonnes :
                Platform, Display et Video.<br />
                Si d'autres colonnes sont rajoutées, il faut veiller à ce
                qu'elles aient toutes un nom différent (pas 2 colonnes sans
                titre par ex).<br />
                <br />
                Dans la colonne "Platform" seront listés tous les partenaires
                sur lesquels une répartition doit être faite. Attention, leur
                nom doit correspondre à celui déclaré dans la DB RMR.<br />
                Elle contiendra aussi ensuite la liste des partenaires à
                répartir sur le CA Amazon. Leur nom sera celui du partenaire
                (correspondant à celui utilisé dans la DB RMR aussi), suffixé
                par "_amazon".<br />
                <br />
                Ensuite, il y aura les 2 colonnes "Display" et "Video" qui
                serviront pour la répartition des revenus de la nature du même
                nom.<br />
                Les montants doivent être renseignés avec des valeurs valides.
                Les caractères acceptés sont les chiffres (0-9), le point (.),
                la virgule (,), l'espace ( ) et le signe "euro" (€).
              </v-container>
            </v-col>
            <v-col cols="12" v-show="isVisibleSubStep(2, 1)">
              <v-container class="text-center">
                <v-switch
                  v-model="config.platformData.confirm"
                  label="SeedTag a bien été mis à jour ?"
                  @change="checkNext('step-2-1')"
                  :disabled="isSubStepDisabled(2, 1)"
                />
              </v-container>
            </v-col>
            <v-col cols="12" v-show="isVisibleSubStep(2, 2)">
              <v-container class="text-center">
                <v-progress-circular
                  v-if="isLoadingStep('step-2-2')"
                  color="primary"
                  indeterminate
                ></v-progress-circular>
                <v-select
                  v-else
                  outlined
                  dense
                  v-model="config.platformData.sheetId"
                  :items="platformData.sheetNames"
                  :menu-props="{ maxHeight: '400', zIndex: 13 }"
                  label="Sheet name"
                  item-text="name"
                  item-value="id"
                  @change="checkNext('step-2-2')"
                  :disabled="isSubStepDisabled(2, 2)"
                ></v-select>
              </v-container>
            </v-col>
          </v-row>

          <prev-next
            v-if="steps.current.step === 2"
            :canPrev="canPrev"
            :canNext="canNext"
            :canReset="canReset"
            @prevStep="prevStep"
            @nextStep="nextStep"
            @resetStep="resetStep"
          />
        </v-card-text>
      </v-card>

      <v-card class="mb-6" v-show="isVisibleStep(3)">
        <v-card-title>Données GAM</v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.gam.prebid.url_display"
                  dense
                  label="URL Prebid Display (CSV)"
                  @keyup="checkNext('step-3-0')"
                  :disabled="isSubStepDisabled(3, 0)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(3, 0) && !isSubStepDisabled(3, 0)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 3 colonnes : "Advertiser" (ou
                SSP), "Ad unit" (ou Site) et "Total CPM, CPC, CPD, and vCPM
                revenue (€)" (ou CA).<br />
                <br />
                Dans la colonne "Advertiser" seront listés tous les partenaires.
                Le contenu sera parsé pour retrouver le nom du partenaire tel
                qu'enregistré dans la DB RMR (à moins qu'un mapping ait été créé
                pour l'occasion). <br />
                <br />
                La colonne "Ad unit" contiendra les noms des sites tels qu'ils
                ont été enregistrés dans la DB RMR.<br />
                <br />
                Enfin, la colonne "Total CPM, CPC, CPD, and vCPM revenue (€)"
                contiendra les montants qui devront être renseignés avec des
                valeurs valides. Les caractères acceptés sont les chiffres
                (0-9), le point (.), la virgule (,) et l'espace ( ).
              </v-container>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" v-show="isVisibleSubStep(3, 1)"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.gam.prebid_by_pubstack.url_display"
                  dense
                  label="URL Prebid by Pubstack Display (CSV)"
                  @keyup="checkNext('step-3-1')"
                  :disabled="isSubStepDisabled(3, 1)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(3, 1) && !isSubStepDisabled(3, 1)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 3 colonnes : "Key-values" (ou
                SSP), "Ad unit" (ou Site) et "Ad server CPM and CPC revenue (€)"
                (ou CA).<br />
                <br />
                Dans la colonne "Key-values" seront listés tous les partenaires.
                Le contenu sera parsé pour retrouver le nom du partenaire tel
                qu'enregistré dans la DB RMR (à moins qu'un mapping ait été créé
                pour l'occasion). <br />
                <br />
                La colonne "Ad unit" contiendra les noms des sites tels qu'ils
                ont été enregistrés dans la DB RMR.<br />
                <br />
                Enfin, la colonne "Ad server CPM and CPC revenue (€)" contiendra
                les montants qui devront être renseignés avec des valeurs
                valides. Les caractères acceptés sont les chiffres (0-9), le
                point (.), la virgule (,) et l'espace ( ).
              </v-container>
            </v-col>
          </v-row>

          <prev-next
            v-if="steps.current.step === 3"
            :canPrev="canPrev"
            :canNext="canNext"
            :canReset="canReset"
            @prevStep="prevStep"
            @nextStep="nextStep"
            @resetStep="resetStep"
          />
        </v-card-text>
      </v-card>

      <v-card class="mb-6" v-show="isVisibleStep(4)">
        <v-card-title>Données Amazon</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.amazon.url_display"
                  dense
                  label="URL display (CSV)"
                  @keyup="checkNext('step-4-0')"
                  :disabled="isSubStepDisabled(4, 0)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(4, 0) && !isSubStepDisabled(4, 0)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 3 colonnes : "Bidder" (ou SSP),
                "Site Name" (ou Site) et "Earnings" (ou CA).<br />
                <br />
                Dans la colonne "Bidder" seront listés tous les partenaires tels
                que leur nom corresponde à celui enregistré dans la DB RMR (à
                moins qu'un mapping ait été créé pour l'occasion). <br />
                <br />
                La colonne "Site Name" contiendra les noms des sites tels qu'ils
                ont été enregistrés dans la DB RMR.<br />
                <br />
                Enfin, la colonne "Earnings" contiendra les montants qui devront
                être renseignés avec des valeurs valides. Les caractères
                acceptés sont les chiffres (0-9), le point (.), la virgule (,)
                et l'espace ( ).
              </v-container>
            </v-col>
            <v-col cols="12" v-show="isVisibleSubStep(4, 1)"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.amazon.url_video"
                  dense
                  label="URL vidéo (CSV)"
                  @keyup="checkNext('step-4-1')"
                  :disabled="isSubStepDisabled(4, 1)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(4, 1) && !isSubStepDisabled(4, 1)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 3 colonnes : "Bidder" (ou SSP),
                "Site Name" (ou Site) et "Earnings" (ou CA).<br />
                <br />
                Dans la colonne "Bidder" seront listés tous les partenaires tels
                que leur nom corresponde à celui enregistré dans la DB RMR (à
                moins qu'un mapping ait été créé pour l'occasion). <br />
                <br />
                La colonne "Site Name" contiendra les noms des sites tels qu'ils
                ont été enregistrés dans la DB RMR.<br />
                <br />
                Enfin, la colonne "Earnings" contiendra les montants qui devront
                être renseignés avec des valeurs valides. Les caractères
                acceptés sont les chiffres (0-9), le point (.), la virgule (,)
                et l'espace ( ).
              </v-container>
            </v-col>
            <v-col cols="12" v-show="isVisibleSubStep(4, 2)">
              <v-container class="text-center">
                <v-switch
                  v-model="config.amazon.confirm"
                  label="Les données Amazon ont bien été nettoyées ?"
                  @change="checkNext('step-4-2')"
                  :disabled="isSubStepDisabled(4, 2)"
                />
              </v-container>
            </v-col>
          </v-row>

          <prev-next
            v-if="steps.current.step === 4"
            :canPrev="canPrev"
            :canNext="canNext"
            :canReset="canReset"
            @prevStep="prevStep"
            @nextStep="nextStep"
            @resetStep="resetStep"
          />
        </v-card-text>
      </v-card>

      <v-card class="mb-6" v-show="isVisibleStep(5)">
        <v-card-title>Données Browsi</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.browsi.url_o_o_browsi_demand"
                  dense
                  label="URL O_O BrowsiDemand (CSV)"
                  @keyup="checkNext('step-5-0')"
                  :disabled="isSubStepDisabled(5, 0)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(5, 0) && !isSubStepDisabled(5, 0)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 2 colonnes : "domain" (ou Site) et
                "net_revenue" (ou CA).<br />
                <br />
                La colonne "domain" contiendra les noms de domaine des sites
                tels qu'ils ont été enregistrés dans la DB RMR.<br />
                <br />
                Enfin, la colonne "net_revenue" contiendra les montants qui
                devront être renseignés avec des valeurs valides. Les caractères
                acceptés sont les chiffres (0-9), le point (.), la virgule (,)
                et l'espace ( ).
              </v-container>
            </v-col>
            <v-col cols="12" v-show="isVisibleSubStep(5, 1)"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.browsi.url_rev_optimize_browsi_demand"
                  dense
                  label="URL RevOptimize BrowsiDemand (CSV)"
                  @keyup="checkNext('step-5-1')"
                  :disabled="isSubStepDisabled(5, 1)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(5, 1) && !isSubStepDisabled(5, 1)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 2 colonnes : "domain" (ou Site) et
                "net_revenue" (ou CA).<br />
                <br />
                La colonne "domain" contiendra les noms de domaine des sites
                tels qu'ils ont été enregistrés dans la DB RMR.<br />
                <br />
                Enfin, la colonne "net_revenue" contiendra les montants qui
                devront être renseignés avec des valeurs valides. Les caractères
                acceptés sont les chiffres (0-9), le point (.), la virgule (,)
                et l'espace ( ).
              </v-container>
            </v-col>
            <v-col cols="12" v-show="isVisibleSubStep(5, 2)"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.browsi.url_o_o_reworld_demand"
                  dense
                  label="URL O_O ReworldDemand (CSV)"
                  @keyup="checkNext('step-5-2')"
                  :disabled="isSubStepDisabled(5, 2)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(5, 2) && !isSubStepDisabled(5, 2)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 3 colonnes : "domain" (ou Site),
                "advertiser_name" (ou SSP) et "net_revenue" (ou CA).<br />
                <br />
                Dans la colonne "advertiser_name" seront listés tous les
                partenaires. Le contenu sera parsé pour retrouver le nom du
                partenaire tel qu'enregistré dans la DB RMR (à moins qu'un
                mapping ait été créé pour l'occasion). <br />
                <br />
                La colonne "domain" contiendra les noms de domaine des sites
                tels qu'ils ont été enregistrés dans la DB RMR.<br />
                <br />
                Enfin, la colonne "net_revenue" contiendra les montants qui
                devront être renseignés avec des valeurs valides. Les caractères
                acceptés sont les chiffres (0-9), le point (.), la virgule (,)
                et l'espace ( ).
              </v-container>
            </v-col>
            <v-col cols="12" v-show="isVisibleSubStep(5, 3)"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.browsi.url_rev_optimize_reworld_demand"
                  dense
                  label="URL RevOptimize ReworldDemand (CSV)"
                  @keyup="checkNext('step-5-3')"
                  :disabled="isSubStepDisabled(5, 3)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(5, 3) && !isSubStepDisabled(5, 3)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 3 colonnes : "domain" (ou Site),
                "advertiser_name" (ou SSP) et "net_revenue" (ou CA).<br />
                <br />
                Dans la colonne "advertiser_name" seront listés tous les
                partenaires. Le contenu sera parsé pour retrouver le nom du
                partenaire tel qu'enregistré dans la DB RMR (à moins qu'un
                mapping ait été créé pour l'occasion). <br />
                <br />
                La colonne "domain" contiendra les noms de domaine des sites
                tels qu'ils ont été enregistrés dans la DB RMR.<br />
                <br />
                Enfin, la colonne "net_revenue" contiendra les montants qui
                devront être renseignés avec des valeurs valides. Les caractères
                acceptés sont les chiffres (0-9), le point (.), la virgule (,)
                et l'espace ( ).
              </v-container>
            </v-col>
          </v-row>

          <prev-next
            v-if="steps.current.step === 5"
            :canPrev="canPrev"
            :canNext="canNext"
            :canReset="canReset"
            @prevStep="prevStep"
            @nextStep="nextStep"
            @resetStep="resetStep"
          />
        </v-card-text>
      </v-card>

      <v-card class="mb-6" v-show="isVisibleStep(6)">
        <v-card-title>Données Xandr</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.xandr.url"
                  dense
                  label="URL Xandr (CSV)"
                  @keyup="checkNext('step-6-0')"
                  :disabled="isSubStepDisabled(6, 0)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(6, 0) && !isSubStepDisabled(6, 0)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 3 colonnes : "Publisher" (ou
                Site), "Header Bidding Demand Partner" (ou SSP) et "Seller
                Revenue (USD)" (ou "Seller Revenue" ou encore CA).<br />
                <br />
                Dans la colonne "Header Bidding Demand Partner" seront listés
                tous les partenaires. Le contenu sera parsé pour retrouver le
                nom du partenaire tel qu'enregistré dans la DB RMR (à moins
                qu'un mapping ait été créé pour l'occasion). <br />
                <br />
                La contenu de la colonne "Publisher" sera parsé afin de
                retrouver les noms de domaine des sites tels qu'ils ont été
                enregistrés dans la DB RMR.<br />
                <br />
                Enfin, la colonne "Seller Revenue" contiendra les montants qui
                devront être renseignés avec des valeurs valides. Les caractères
                acceptés sont les chiffres (0-9), le point (.), la virgule (,)
                et l'espace ( ).
              </v-container>
            </v-col>
          </v-row>

          <prev-next
            v-if="steps.current.step === 6"
            :canPrev="canPrev"
            :canNext="canNext"
            :canReset="canReset"
            @prevStep="prevStep"
            @nextStep="nextStep"
            @resetStep="resetStep"
          />
        </v-card-text>
      </v-card>

      <v-card class="mb-6" v-show="isVisibleStep(7)">
        <v-card-title>Données Actirise</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.actirise.revenues"
                  dense
                  label="URL Actirise (CSV)"
                  @keyup="checkNext('step-7-0')"
                  :disabled="isSubStepDisabled(7, 0)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(7, 0) && !isSubStepDisabled(7, 0)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 2 colonnes : "Domain" (ou Site),
                et "CA".<br />
                <br />
                La colonne "Domain" contient le nom de domaine des sites tels
                que déclarés dans la DB de RMR.<br />
                <br />
                Et la colonne "CA" contiendra les montants qui devront être
                renseignés avec des valeurs valides. Les caractères acceptés
                sont les chiffres (0-9), le point (.), la virgule (,) et
                l'espace ( ).
              </v-container>
            </v-col>
            <v-col cols="12" v-show="isVisibleSubStep(7, 1)"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.actirise.fees"
                  dense
                  label="URL Actirise Fees (CSV)"
                  @keyup="checkNext('step-7-1')"
                  :disabled="isSubStepDisabled(7, 1)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(7, 1) && !isSubStepDisabled(7, 1)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 2 colonnes : "Domain", et
                "Estimated Fees Direct sold" (ou "Estimated Fees Ad server").<br />
                <br />
                La colonne "Domain" contient le nom de domaine des sites tels
                que déclarés dans la DB de RMR.<br />
                <br />
                Et la colonne "Estimated Fees Direct sold" contiendra les
                montants qui devront être renseignés avec des valeurs valides.
                Les caractères acceptés sont les chiffres (0-9), le point (.),
                la virgule (,) et l'espace ( ).
              </v-container>
            </v-col>
          </v-row>

          <prev-next
            v-if="steps.current.step === 7"
            :canPrev="canPrev"
            :canNext="canNext"
            :canReset="canReset"
            @prevStep="prevStep"
            @nextStep="nextStep"
            @resetStep="resetStep"
          />
        </v-card-text>
      </v-card>

      <v-card class="mb-6" v-show="isVisibleStep(8)">
        <v-card-title>Bidder Domain (from Actirise)</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.bidder_domain.url"
                  dense
                  label="Bidder Domain (CSV)"
                  @keyup="checkNext('step-8-0')"
                  :disabled="isSubStepDisabled(8, 0)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(8, 0) && !isSubStepDisabled(8, 0)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 3 colonnes : "Bidder", "Domain" et
                "SSP seat revenue" (ou "External RTB Revenue").<br />
                <br />
                Dans la colonne "Bidder" seront listés tous les partenaires. Le
                contenu sera parsé pour retrouver le nom du partenaire tel
                qu'enregistré dans la DB RMR (à moins qu'un mapping ait été créé
                pour l'occasion). <br />
                <br />
                La colonne "Domain" contient le nom de domaine des sites tels
                que déclarés dans la DB de RMR.<br />
                <br />
                Et la colonne "SSP seat revenue" contiendra les montants qui
                devront être renseignés avec des valeurs valides. Les caractères
                acceptés sont les chiffres (0-9), le point (.), la virgule (,)
                et l'espace ( ).
              </v-container>
            </v-col>
          </v-row>

          <prev-next
            v-if="steps.current.step === 8"
            :canPrev="canPrev"
            :canNext="canNext"
            :canReset="canReset"
            @prevStep="prevStep"
            @nextStep="nextStep"
            @resetStep="resetStep"
          />
        </v-card-text>
      </v-card>

      <v-card class="mb-6" v-show="isVisibleStep(9)">
        <v-card-title>Viously</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12"
              ><v-container class="text-center">
                <v-text-field
                  outlined
                  v-model="config.viously.ssp_seat"
                  dense
                  label="SSP seats (CSV)"
                  @keyup="checkNext('step-9-0')"
                  :disabled="isSubStepDisabled(9, 0)"
                ></v-text-field>
              </v-container>
              <v-container
                class="text-xs"
                v-show="isVisibleSubStep(9, 0) && !isSubStepDisabled(9, 0)"
              >
                Ce fichier est un CSV dont le séparateur doit être la virgule
                (,).<br />
                Il doit contenir au moins ces 3 colonnes : "domain" (ou "Publisher"),
                "publisher_ssp_seat" (ou "Bidder") et "seat_revenues" (ou "Seat revenues").<br />
                <br />
                La colonne "domain" contient le nom de domaine des sites tels
                que déclarés dans la DB de RMR, ou définis dans les mappings.<br />
                Dans la colonne "publisher_ssp_seat" seront listés tous les
                partenaires. Le contenu sera parsé pour retrouver le nom du
                partenaire tel qu'enregistré dans la DB RMR (à moins qu'un
                mapping ait été créé pour l'occasion). <br />
                <br />
                <br />
                Et la colonne "seat_revenues" contiendra les montants qui
                devront être renseignés avec des valeurs valides. Les caractères
                acceptés sont les chiffres (0-9), le point (.), la virgule (,)
                et l'espace ( ).
              </v-container>
            </v-col>
          </v-row>

          <prev-next
            v-if="steps.current.step === 9"
            :canPrev="canPrev"
            :canNext="canNext"
            :canReset="canReset"
            @prevStep="prevStep"
            @nextStep="nextStep"
            @resetStep="resetStep"
          />
        </v-card-text>
      </v-card>

      <v-btn
        v-if="canSubmit"
        color="primary"
        class="me-3 mt-3"
        @click.native="submit"
      >
        Générer le rapport</v-btn
      >
    </v-form>

    <consolidation-table
      v-show="isResultVisible"
      :config="config"
      :applyFilters="submitting"
      @reloaded="setReloaded"
    />
  </div>
</template>

<script>
import axios from "@axios";
import every from "lodash/every";
import { required } from "@core/utils/validation";

import { mdiHelpCircleOutline } from "@mdi/js";

import DateFilter from "@/components/common/filters/DateFilter";
import PrevNext from "@/components/finance/report/actions/PrevNext";
import ConsolidationTable from "@/components/finance/report/tables/Consolidation.vue";
import ConsolidationHelpModal from "@/components/finance/report/ConsolidationHelpModal.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import {
  getDate,
  getDateDetails,
  getMonthDateStr,
  monthsFrArray,
} from "@/utils/dates";

const INIT_MODES = {
  RUN_LAST_CONFIG: "run_last_config",
  LOAD_LAST_CONFIG: "load_last_config",
  RUN_JSON_CONFIG: "run_json_config",
  LOAD_JSON_CONFIG: "load_json_config",
  LOAD_DEFAULT_CONFIG: "load_default_config",
};

const INIT_OPT_GROUPS = {
  RUN: "Lancement tel quel",
  LOAD: "Modifiable point par point",
};

// Nb of subSteps for each step :
const DEFAULT_STEPS = [
  0, // Step 0 => INIT (may change depending on selected INIT_MODES)
  2, // Step 1 => Config
  2, // Step 2 => DATA FROM PLATFORMS
  1, // Step 3 => GAM Repartition
  2, // Step 4 => Amazon
  3, // Step 5 => Browsi
  0, // Step 6 => Xandr
  1, // Step 7 => Actirise
  0, // Step 8 => Seedtag
  0, // Step 9 => Viously
];

const JSON_CONFIG_STEPS = [
  1, // Step 0 => INIT (only json upload field)
];

const LAST_CONFIG_STEPS = [
  0, // Step 0 => INIT (directly run last saved config)
];

const DEFAULT_CONFIG = {
  export_file_name: "Consolidation_Financiere.xlsx",
  monthDate: "",
  monthName: "",
  currency_conversion_rate: 1, // 0.92495,
  platformData: {
    url: "",
    sheetName: "",
    confirm: false,
    sheetId: 0,
  },
  gam: {
    prebid: {
      url_display: "",
    },
    prebid_by_pubstack: {
      url_display: "",
    },
  },
  amazon: {
    url_display: "",
    url_video: "",
    confirm: false,
  },
  browsi: {
    url_o_o_browsi_demand: "",
    url_rev_optimize_browsi_demand: "",
    url_o_o_reworld_demand: "",
    url_rev_optimize_reworld_demand: "",
  },
  xandr: {
    url: "",
  },
  actirise: {
    revenues: "",
    fees: "",
  },
  bidder_domain: {
    url: "",
  },
  viously: {
    ssp_seat: "",
  },
};

export default {
  name: "ConsolidationReport",
  components: {
    ConsolidationTable,
    ConsolidationHelpModal,
    DateFilter,
    PrevNext,
    RmrIconButton,
  },
  data() {
    return {
      icons: { mdiHelpCircleOutline },
      showHelp: false,
      submitting: false,
      dataLoaded: false,
      SUB_STEPS: DEFAULT_STEPS,
      steps: {
        current: {
          loading: false,
          step: 0,
          subStep: 0,
        },
        history: [],
        canNext: false,
      },
      initModes: [
        { header: INIT_OPT_GROUPS.RUN },
        {
          id: INIT_MODES.RUN_LAST_CONFIG,
          name: "Relancer avec la dernière config",
          group: INIT_OPT_GROUPS.RUN,
          disabled: false,
        },
        {
          id: INIT_MODES.RUN_JSON_CONFIG,
          name: "Lancer avec une config JSON",
          group: INIT_OPT_GROUPS.RUN,
          disabled: false,
        },
        { divider: true },
        { header: INIT_OPT_GROUPS.LOAD },
        {
          id: INIT_MODES.LOAD_LAST_CONFIG,
          name: "Précharger la dernière config",
          group: INIT_OPT_GROUPS.LOAD,
          disabled: false,
        },
        {
          id: INIT_MODES.LOAD_JSON_CONFIG,
          name: "Précharger une config JSON",
          group: INIT_OPT_GROUPS.LOAD,
          disabled: false,
        },
        {
          id: INIT_MODES.LOAD_DEFAULT_CONFIG,
          name: "Repartir de zéro",
          group: INIT_OPT_GROUPS.LOAD,
          disabled: false,
        },
      ],
      initMode: INIT_MODES.LOAD_DEFAULT_CONFIG,
      config_json: "",
      config: DEFAULT_CONFIG,
      platformData: {
        sheetNames: [],
      },
      required,
      dropdownSelected: (v) => {
        return !!v;
      },
    };
  },
  async mounted() {
    this.initMonthDate();

    const lastStoredConfig =
      localStorage.getItem("config-conso-financiere") ?? "";
    if (lastStoredConfig) {
      // Change default option to use last stored config by default
      this.initMode = INIT_MODES.RUN_LAST_CONFIG;
    } else {
      // Disbale option entries if no previous config has been stored yet :
      this.initModes = this.initModes.map((option) => {
        if (
          [INIT_MODES.LOAD_LAST_CONFIG, INIT_MODES.RUN_LAST_CONFIG].includes(
            option.id ?? "",
          )
        ) {
          return {
            ...option,
            disabled: true,
          };
        }

        return option;
      });
    }

    await this.checkNewStep();
  },
  methods: {
    initMonthDate() {
      const now = new Date();
      const lastDayOfPrevMonth = getDate(-now.getDate());
      this.config.monthDate = getMonthDateStr(lastDayOfPrevMonth);
      const { month } = getDateDetails(lastDayOfPrevMonth);
      this.config.monthName = monthsFrArray[parseInt(month, 10) - 1];
    },
    async submit() {
      this.submitting = true;

      localStorage.setItem(
        "config-conso-financiere",
        JSON.stringify(this.config),
      );
      // console.log("Saved config", this.config);
    },
    isValidConfig(config) {
      const key_validations = Object.keys(DEFAULT_CONFIG).map(
        (key) => key in config,
      );

      return every(key_validations);
    },
    loadConfig(config) {
      if (!this.isValidConfig(config)) {
        alert(
          "La configuration choisie n'est plus valide : chargement de la config par défaut",
        );
        this.loadDefaultConfig();

        return;
      }

      this.config = config;
      if (
        ![INIT_MODES.RUN_LAST_CONFIG, INIT_MODES.RUN_JSON_CONFIG].includes(
          this.initMode,
        )
      ) {
        // Force user to reselect sheetName/sheetId
        this.config.platformData.sheetName = "";
        this.config.platformData.sheetId = 0;
        // Force user to confirm again checks done outside of RMR
        this.config.platformData.confirm = false;
        this.config.amazon.confirm = false;
      }

      // console.log("loadConfig", this.config);
    },
    loadDefaultConfig() {
      this.config = DEFAULT_CONFIG;
      this.initMode = INIT_MODES.LOAD_DEFAULT_CONFIG;
      this.initMonthDate();
    },
    loadLastConfig() {
      const lastStoredConfig =
        localStorage.getItem("config-conso-financiere") ?? "";
      if (!lastStoredConfig) {
        alert(
          "Aucune configuration n'a encore été enregistrée : chargement de la config par défaut",
        );
        this.loadDefaultConfig();

        return;
      }

      // console.log("last stored config", lastStoredConfig);
      this.loadConfig(JSON.parse(lastStoredConfig));
    },
    async ["step-2-2"]() {
      this.steps.current.loading = true;
      const { data } = await axios.get(
        `/finance/data-from-platform/sheetnames`,
        { params: { url: this.config.platformData.url } },
      );
      this.platformData.sheetNames = data.items;

      this.steps.current.loading = false;
    },
    async checkNewStep() {
      const ref = this.stepRefName;
      if (ref in this) {
        await this[ref]();
      }

      this.checkNext();
    },
    getRefValue() {
      switch (this.stepRefName) {
        case "step-0-0":
          return this.initMode;
        case "step-0-1":
          return this.config_json;

        case "step-1-0":
          return this.config.export_file_name;
        case "step-1-1":
          return this.config.monthDate;
        case "step-1-2":
          return this.config.currency_conversion_rate;

        case "step-2-0":
          return this.config.platformData.url;
        case "step-2-1":
          return this.config.platformData.confirm;
        case "step-2-2":
          return this.config.platformData.sheetId;

        case "step-3-0":
          return this.config.gam.prebid.url_display;
        case "step-3-1":
          return this.config.gam.prebid_by_pubstack.url_display;

        case "step-4-0":
          return this.config.amazon.url_display;
        case "step-4-1":
          return this.config.amazon.url_video;
        case "step-4-2":
          return this.config.amazon.confirm;

        case "step-5-0":
          return this.config.browsi.url_o_o_browsi_demand;
        case "step-5-1":
          return this.config.browsi.url_rev_optimize_browsi_demand;
        case "step-5-2":
          return this.config.browsi.url_o_o_reworld_demand;
        case "step-5-3":
          return this.config.browsi.url_rev_optimize_reworld_demand;

        case "step-6-0":
          return this.config.xandr.url;

        case "step-7-0":
          return this.config.actirise.revenues;
        case "step-7-1":
          return this.config.actirise.fees;

        case "step-8-0":
          return this.config.bidder_domain.url;

        case "step-9-0":
          return this.config.viously.ssp_seat;

        default:
          return "";
      }
    },
    checkNext() {
      this.steps.canNext = !!this.getRefValue();
    },
    resetStep() {
      this.steps.current = {
        step: 0,
        subStep: 0,
      };
      this.steps.history = [];
      this.steps.canNext = false;
    },
    async nextStep() {
      const maxSubStep = this.SUB_STEPS[this.steps.current.step];
      if (this.steps.current.subStep >= maxSubStep) {
        if (this.steps.current.step < this.SUB_STEPS.length) {
          // if (this.steps.current.step > 0) {
          // Don't go back to init: first step is enough
          this.steps.history.push({ ...this.steps.current });
          // }

          this.steps.current.step += 1;
          this.steps.current.subStep = 0;
          this.steps.canNext = false;

          await this.checkNewStep();
        }

        return;
      }

      this.steps.history.push({ ...this.steps.current });
      this.steps.current.subStep += 1;
      this.steps.canNext = false;

      await this.checkNewStep();
    },
    prevStep() {
      if (this.steps.history.length > 0) {
        this.steps.current = { ...this.steps.history.pop() };

        this.checkNext();
      }
    },
    updateMonthDate(newDateStr) {
      this.config.monthDate = newDateStr;
    },
    async setReloaded() {
      this.dataLoaded = true;
      this.submitting = false;

      this.resetStep();
      await this.checkNewStep();
    },
  },
  computed: {
    stepRefName() {
      return `step-${this.steps.current.step}-${this.steps.current.subStep}`;
    },
    isLoadingStep() {
      return (step) => this.stepRefName === step && this.steps.current.loading;
    },
    isVisibleStep() {
      return (step) => this.steps.current.step === step;
    },
    isVisibleSubStep() {
      return (step, subStep) =>
        this.steps.current.step > step || this.steps.current.subStep >= subStep;
    },
    isSubStepDisabled() {
      return (step, subStep) =>
        this.steps.current.step > step || this.steps.current.subStep > subStep;
    },
    isResultVisible() {
      return this.submitting || this.dataLoaded;
    },
    canSubmit() {
      return (
        this.steps.current.step === this.SUB_STEPS.length - 1 &&
        this.steps.current.subStep ===
          this.SUB_STEPS[this.steps.current.step] &&
        !this.submitting &&
        this.steps.canNext
      );
    },
    canNext() {
      if (!this.steps.canNext) {
        return false;
      }

      const maxSubStep = this.SUB_STEPS[this.steps.current.step];

      return (
        this.steps.current.subStep < maxSubStep ||
        this.steps.current.step < this.SUB_STEPS.length - 1
      );
    },
    canPrev() {
      return this.steps.history.length > 0;
    },
    canReset() {
      return this.steps.current.step > 0 || this.steps.current.subStep > 0;
    },
    showJsonConfigProcess() {
      return [INIT_MODES.LOAD_JSON_CONFIG, INIT_MODES.RUN_JSON_CONFIG].includes(
        this.initMode,
      );
    },
  },
  watch: {
    ["config.platformData.sheetId"]() {
      if (
        ![INIT_MODES.RUN_LAST_CONFIG, INIT_MODES.RUN_JSON_CONFIG].includes(
          this.initMode,
        )
      ) {
        const sheet = this.platformData.sheetNames.find(
          (item) => item.id === this.config.platformData.sheetId,
        );
        this.config.platformData.sheetName = sheet?.name ?? "";
      }
    },
    ["config.monthDate"]() {
      const [_, month] = this.config.monthDate.split("-");
      this.config.monthName = monthsFrArray[parseInt(month, 10) - 1];
    },
    initMode() {
      switch (this.initMode) {
        case INIT_MODES.LOAD_JSON_CONFIG:
          this.SUB_STEPS = DEFAULT_STEPS.map((val, index) =>
            index === 0 ? 1 : val,
          ); // Show JSON field + other steps
          break;

        case INIT_MODES.RUN_JSON_CONFIG:
          this.SUB_STEPS = JSON_CONFIG_STEPS; // Show only JSON field
          break;

        default:
          if (
            [INIT_MODES.LOAD_LAST_CONFIG, INIT_MODES.RUN_LAST_CONFIG].includes(
              this.initMode,
            )
          ) {
            this.loadLastConfig();
          } else {
            this.loadDefaultConfig();
          }

          this.SUB_STEPS =
            this.initMode === INIT_MODES.RUN_LAST_CONFIG
              ? LAST_CONFIG_STEPS // Show nothing more : directly run report
              : DEFAULT_STEPS;
          break;
      }
    },
    config_json() {
      if (this.showJsonConfigProcess && this.config_json.length > 0) {
        this.loadConfig(JSON.parse(this.config_json));
      }
    },
  },
};
</script>

<style scoped></style>
